import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  BarChart3,
  BookOpen,
  Bot,
  Film,
  ChevronLeft,
  ChevronRight,
  Clock,
  ExternalLink,
  History,
  LayoutDashboard,
  LogOut,
  MessageCircle,
  Package,
  Percent,
  Share2,
  Settings,
  ShoppingCart,
  Ticket,
  User as UserIcon,
  UserPlus,
  Users,
  X,
  Menu,
} from "lucide-react";
import { ASSETS } from "@/lib/assets";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { clearAdminToken } from "@/api/client";

const SIDEBAR_STORAGE_KEY = "certi_admin_sidebar_collapsed";

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  end?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: "/admin", label: "Visão geral", icon: LayoutDashboard, end: true },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/admin/pedidos", label: "Pedidos", icon: ShoppingCart },
  { to: "/admin/produtos", label: "Produtos", icon: Package },
  { to: "/admin/promocoes", label: "Promoções", icon: Percent },
  { to: "/admin/cupons", label: "Cupons", icon: Ticket },
  { to: "/admin/leads", label: "Leads", icon: UserPlus },
  { to: "/admin/operacao", label: "Horários & operação", icon: Clock },
  { to: "/admin/clientes", label: "Clientes", icon: Users },
  { to: "/admin/blog", label: "Blog", icon: BookOpen },
  { to: "/admin/blog/automacao", label: "Blog Automação", icon: Bot },
  { to: "/admin/reels", label: "Reels IG/FB", icon: Film },
  { to: "/admin/redes-sociais", label: "Redes sociais", icon: Share2 },
  { to: "/admin/whatsapp", label: "WhatsApp", icon: MessageCircle },
  { to: "/admin/auditoria", label: "Auditoria", icon: History },
  { to: "/admin/configuracoes", label: "Configurações", icon: Settings },
];

export function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();

  // Persisted collapsed state for desktop. Mobile uses overlay open/closed.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SIDEBAR_STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, collapsed ? "1" : "0");
    } catch {
      /* ignore quota errors */
    }
  }, [collapsed]);

  // Close mobile drawer on every route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    clearAdminToken();
    navigate("/admin/login");
  };

  const sidebarWidthClass = collapsed ? "lg:w-[76px]" : "lg:w-[260px]";

  return (
    <div className="flex min-h-screen bg-muted/30">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-foreground/40 backdrop-blur-sm lg:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex flex-col border-r border-border/60 bg-footer text-footer-foreground transition-all duration-300 ease-out",
          // Mobile: slide-in
          mobileOpen ? "w-[260px] translate-x-0" : "-translate-x-full w-[260px]",
          // Desktop: always visible, width depends on collapsed
          "lg:translate-x-0",
          sidebarWidthClass
        )}
      >
        {/* Brand gradient accent */}
        <div aria-hidden className="absolute inset-x-0 top-0 h-1 bg-gradient-brand" />

        {/* Header */}
        <div className={cn("flex h-20 items-center border-b border-white/5", collapsed ? "lg:justify-center px-3" : "px-5")}>
          <Link to="/admin" className="flex items-center gap-3 overflow-hidden">
            <img
              src={ASSETS.faviconPng}
              alt="Biz Certificadora Digital"
              className="h-10 w-10 shrink-0 object-contain"
            />
            {!collapsed && (
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-[0.18em] text-footer-foreground/50">
                  Painel
                </p>
                <p className="truncate text-sm font-bold text-footer-foreground">
                  Biz Certificadora
                </p>
              </div>
            )}
          </Link>
          <button
            type="button"
            className="ml-auto rounded-md p-1 text-footer-foreground/60 hover:bg-white/10 hover:text-footer-foreground lg:hidden"
            onClick={() => setMobileOpen(false)}
            aria-label="Fechar menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-3 py-5 space-y-1">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  "group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all",
                  collapsed && "lg:justify-center lg:px-2",
                  isActive
                    ? "bg-gradient-brand text-white shadow-brand"
                    : "text-footer-foreground/70 hover:bg-white/5 hover:text-footer-foreground"
                )
              }
              title={collapsed ? item.label : undefined}
            >
              <item.icon className="h-5 w-5 shrink-0" />
              {!collapsed && <span className="truncate">{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Footer / actions */}
        <div className="border-t border-white/5 p-3 space-y-2">
          {/* Quick links */}
          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            className={cn(
              "flex items-center gap-3 rounded-xl px-3 py-2 text-xs font-medium text-footer-foreground/60 hover:bg-white/5 hover:text-footer-foreground transition-colors",
              collapsed && "lg:justify-center lg:px-2"
            )}
            title={collapsed ? "Abrir loja" : undefined}
          >
            <ExternalLink className="h-4 w-4 shrink-0" />
            {!collapsed && <span>Abrir loja</span>}
          </a>

          {/* User dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className={cn(
                  "flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition-colors hover:bg-white/5",
                  collapsed && "lg:justify-center lg:px-2"
                )}
              >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-brand text-xs font-bold text-white">
                  A
                </div>
                {!collapsed && (
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-semibold text-footer-foreground">Admin</p>
                    <p className="truncate text-[10px] text-footer-foreground/50">
                      admin@certificadoradigital.com
                    </p>
                  </div>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="top" className="w-56">
              <DropdownMenuLabel className="text-xs">Conta</DropdownMenuLabel>
              <DropdownMenuItem asChild>
                <Link to="/admin/configuracoes" className="cursor-pointer">
                  <UserIcon className="h-4 w-4 mr-2" />
                  Perfil
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive">
                <LogOut className="h-4 w-4 mr-2" />
                Sair
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Collapse toggle (desktop only) */}
          <button
            type="button"
            className={cn(
              "hidden w-full items-center gap-3 rounded-xl px-3 py-2 text-xs font-medium text-footer-foreground/50 transition-colors hover:bg-white/5 hover:text-footer-foreground lg:flex",
              collapsed && "justify-center px-2"
            )}
            onClick={() => setCollapsed((c) => !c)}
            title={collapsed ? "Expandir menu" : "Colapsar menu"}
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
            {!collapsed && <span>Recolher menu</span>}
          </button>
        </div>
      </aside>

      {/* Main column */}
      <div className={cn("flex-1 flex flex-col min-w-0", collapsed ? "lg:pl-[76px]" : "lg:pl-[260px]")}>
        {/* Top bar (mobile only — has hamburger) */}
        <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-border bg-background/80 px-4 backdrop-blur-xl lg:hidden">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="flex h-9 w-9 items-center justify-center rounded-md text-foreground hover:bg-muted"
            aria-label="Abrir menu"
          >
            <Menu className="h-5 w-5" />
          </button>
          <Link to="/admin" className="flex items-center gap-2">
            <img src={ASSETS.faviconPng} alt="" className="h-8 w-8 object-contain" />
            <span className="text-sm font-bold">Biz Certificadora</span>
          </Link>
          <div className="ml-auto">
            <Button variant="outline" size="sm" onClick={handleLogout}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </header>

        {/* Main content */}
        <main className="flex-1 px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
          <div className="mx-auto max-w-7xl">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
